import { isEditableElementFocused } from '@src/services/shortcuts/keyboard';
import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import RedoIcon from '../../svgs/RedoIcon';
import UndoIcon from '../../svgs/UndoIcon';
import VerticalLine from '../../svgs/VerticalLine';
import { COMPACT_TOOLTIP_STYLE } from './tooltipStyles';

interface UndoRedoButtonsProps {
  canUndo: boolean;
  canRedo: boolean;
  getUndoLabel?: () => string;
  getRedoLabel?: () => string;
  onUndo: () => void;
  onRedo: () => void;
  setHotkeys?: boolean;
  /** Color applied to icon strokes on hover (default: var(--foreground)) */
  hoverColor?: string;
  /** Square hit-target size in px (default: 28). Compact toolbars pass less. */
  buttonSize?: number;
}

export const UndoRedoButtons: React.FC<UndoRedoButtonsProps> = ({
  canUndo,
  canRedo,
  getUndoLabel,
  getRedoLabel,
  onUndo,
  onRedo,
  setHotkeys = false,
  hoverColor,
  buttonSize = 28,
}) => {
  const handleUndo = useCallback(() => {
    if (canUndo) {
      onUndo();
    }
  }, [onUndo, canUndo]);

  const handleRedo = useCallback(() => {
    if (canRedo) {
      onRedo();
    }
  }, [onRedo, canRedo]);

  useEffect(() => {
    if (!setHotkeys) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.code !== 'KeyZ' && e.code !== 'KeyY') return;
      // An editable field owns its own undo, so typing is left alone.
      if (isEditableElementFocused()) return;

      e.preventDefault();
      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y to redo, Ctrl/Cmd + Z to undo.
      // Keyed on `code` rather than `key`, which reads as `Z` once shift is
      // held — so a redo matched on `key === 'z'` never fired.
      if (e.code === 'KeyY' || e.shiftKey) {
        handleRedo();
      } else {
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setHotkeys, handleUndo, handleRedo]);

  const undoLabel = getUndoLabel ? getUndoLabel() : '';
  const redoLabel = getRedoLabel ? getRedoLabel() : '';

  return (
    <UndoRedo>
      <IconButton
        data-testid="transcript-undo-button"
        data-tooltip-id="undo-redo-tooltip"
        data-tooltip-content={
          canUndo ? `Undo ${undoLabel || ''}` : 'Nothing to undo'
        }
        background={false}
        disabled={!canUndo}
        onClick={handleUndo}
        $hoverColor={hoverColor}
        $size={buttonSize}
      >
        <UndoIcon />
      </IconButton>

      <VerticalLine width="2" height="16" viewBox="0 0 2 16" />

      <IconButton
        data-testid="transcript-redo-button"
        data-tooltip-id="undo-redo-tooltip"
        data-tooltip-content={
          canRedo ? `Redo ${redoLabel || ''}` : 'Nothing to redo'
        }
        background={false}
        disabled={!canRedo}
        onClick={handleRedo}
        $hoverColor={hoverColor}
        $size={buttonSize}
      >
        <RedoIcon />
      </IconButton>
      {ReactDOM.createPortal(
        <Tooltip
          id="undo-redo-tooltip"
          style={COMPACT_TOOLTIP_STYLE}
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </UndoRedo>
  );
};

const UndoRedo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  * {
    margin: 0;
    padding: 0;
  }
`;

const IconButton = styled.div<{
  disabled?: boolean;
  background?: boolean;
  margin?: number;
  $hoverColor?: string;
  $size?: number;
}>`
  display: flex;
  width: ${props => props.$size ?? 28}px;
  height: ${props => props.$size ?? 28}px;
  background: transparent;
  border-radius: var(--radius-input);
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  align-items: center;
  justify-content: center;
  transition: all var(--transition-normal);
  opacity: ${props => (props.disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${props =>
      props.disabled ? 'transparent' : 'var(--surface-elevated)'};
  }

  &:hover svg path {
    stroke: ${props =>
      props.disabled ? '' : props.$hoverColor || 'var(--foreground)'};
  }

  &:active {
    transform: ${props => (props.disabled ? 'none' : 'scale(0.95)')};
  }
`;
